import {
  Day,
  DragAndDrop,
  Inject,
  Resize,
  ResourceDirective,
  ResourcesDirective,
  ScheduleComponent,
  ViewDirective,
  ViewsDirective,
} from '@syncfusion/ej2-react-schedule'
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import './scheduleCalendar.scss'

import type { ObjectType } from 'common/interfaces/object'
import CalendarEvent from './CalendarEvent'
import type { ScheduleCalendarProps } from './scheduleCalendar.types'
// import EventTooltip from './EventTooltip'
import sendMessageIcon from 'assets/icons/send-message.svg'
import { Button } from 'common/components/Button/Button'
import { Show } from 'common/components/Show/Show'
import { ALT_CONSTANTS } from 'common/constants/altConstants'
import { BUTTON_CONSTANTS, BUTTON_MODIFIER } from 'common/constants/buttonConstants'
import { DateService } from 'common/services/dateService'
import dayjs from 'dayjs'
import { AnnounceIconButton } from 'features/Home/Book/Booking/Schedule/Announce/announce'
import { AnnounceModal } from 'features/Home/Book/Booking/Schedule/Announce/announce-modal'
import { SendSMSConfirmation } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/SendSMSConfirmation/SendSMSConfirmation'
import { Warning } from 'features/Home/Book/Booking/Schedule/PatientSchedule/Calendar/Warning/Warning'
import { INFO_CONSTANTS, MIN_PRE_POPULATE_TIME } from 'features/Home/constants/infoConstants'
import { useParams } from 'react-router'
import { useToggle } from 'react-use'
import { useScheduleHeight } from '../useScheduleHeight'
import { getAppointmentColor, getAppointmentColorInputFromEvent } from './helpers'
import { useStableScheduleDataSource } from './useStableScheduleDataSource'
import { useScheduleCalendar } from './useScheduleCalendar'

export const ScheduleCalendar = memo(
  ({
    createCustomAppointment,
    setEditedPatient,
    handleUpdateEvents,
    sendInfoPatientsNotification = async () => {},
    prepopulateTimeOnClick = MIN_PRE_POPULATE_TIME,
    showConsultationStaffInitials = false,
    resources = [],
    dayInterval,
    disabledIntervals,
    timeZone,
    rooms,
    isUpdating,
    schedules,
    isSendingNotifications,
    eventsToSendNotification,
    patientStatuses,
    appointmentCategories,
    appointmentColor,
    hasEhrBooking,
  }: ScheduleCalendarProps) => {
    const { day } = useParams()
    const [interval, setInterval] = useState(15)
    const [showBusyNotification, setShowBusyNotification] = useToggle(false)
    const [isSendSmsConfirmVisible, setIsSendSmsConfirmVisible] = useToggle(false)
    const [openAnnounceModal, toggleAnnounceModal] = useToggle(false)

    const scheduleObj = useRef<ScheduleComponent>(null)
    const scheduleContainerRef = useRef<HTMLDivElement>(null)

    const formattedDate = useMemo(() => {
      if (!timeZone || !day) {
        return null
      }

      return dayjs(day).tz(timeZone, true).format('YYYY-MM-DD')
    }, [timeZone, day])

    const {
      events,
      awaitConfirmationEvent,
      handleScheduleChange,
      setAwaitConfirmationEvent,
      proceedToUpdateEvents,
      handleSelect,
    } = useScheduleCalendar({
      schedules,
      dayInterval,
      disabledIntervals,
      formattedDate,
      timeZone,
      rooms,
      isUpdating,
      scheduleObj,
      prepopulateTimeOnClick,
      createCustomAppointment,
      handleUpdateEvents,
    })

    const data = useStableScheduleDataSource(events)

    const resourceHeaderTemplate = useCallback(
      (props: ObjectType) => {
        const selectedResource = props.resourceData.resourceId
        return (
          resources.find(({ resourceId }) => resourceId === selectedResource)?.resourceTemplate ||
          ''
        )
      },
      [resources],
    )

    const indentTemplate = useCallback(() => {
      if (!dayjs(day).isSame(dayjs(), 'day')) return <></>
      return (
        <div className='center'>
          <AnnounceIconButton onClick={toggleAnnounceModal} />
        </div>
      )
    }, [rooms])

    const eventRendered = useCallback(
      (props: any) => {
        const { statusCode, categoryCode } = getAppointmentColorInputFromEvent(
          props.data,
          schedules,
        )
        const { color, background, borderColor } = getAppointmentColor({
          appointmentColor,
          categoryStatuses: appointmentCategories,
          statusColorName: patientStatuses,
          statusCode,
          categoryCode,
        })
        props.element.style.color = color ?? ''
        props.element.style.background = background ?? ''
        props.element.style.border = `4px solid ${borderColor ?? 'var(--light-03)'}`
      },
      [appointmentColor, appointmentCategories, patientStatuses, schedules],
    )

    const eventTemplate = useCallback(
      (props: any) => {
        const { color, borderColor } = getAppointmentColor({
          appointmentColor,
          categoryStatuses: appointmentCategories,
          statusColorName: patientStatuses,
          statusCode: props.status?.code,
          categoryCode: props.category?.code,
        })
        return (
          <CalendarEvent
            event={props}
            statusColor={appointmentColor === 'status' ? color : borderColor}
            hasEhrBooking={hasEhrBooking}
            showConsultationStaffInitials={showConsultationStaffInitials}
          />
        )
      },
      [
        appointmentColor,
        appointmentCategories,
        hasEhrBooking,
        patientStatuses,
        showConsultationStaffInitials,
      ],
    )

    const handleEventClick = useCallback((props: any) => {
      props.originalEvent.preventDefault()
      props.originalEvent.stopPropagation()
      setEditedPatient(props.event)
    }, [])

    const handleChange = useCallback(async (props: any) => {
      if (props.requestType !== 'eventChange') {
        return
      }

      handleScheduleChange(props.data).finally(() => {
        handleRemoveSelectedCells()
      })
    }, [])

    const handleToogleSendSmsConfirmVisible = useCallback(
      (event: boolean) => () => {
        setIsSendSmsConfirmVisible(event)
      },
      [],
    )

    const handleSendNotifications = useCallback(async () => {
      await sendInfoPatientsNotification(eventsToSendNotification)
      setIsSendSmsConfirmVisible(false)
    }, [eventsToSendNotification])

    const handleToggleSendSmsConfirmVisible = useCallback(
      (event: boolean) => () => {
        setIsSendSmsConfirmVisible(event)
      },
      [],
    )

    const handleToggleSendBusyNotification = useCallback(
      (event: boolean) => () => {
        setShowBusyNotification(event)
      },
      [],
    )

    const handleRenderCell = useCallback(
      (args: ObjectType) => {
        if (args.elementType !== 'workCells') {
          return
        }

        const resource = resources[args.groupIndex]
        const room = rooms.find((room: ObjectType) => {
          return resource?.resourceId === `${room.label}/${room.value}`
        })

        if (!room || !room.slots) {
          return
        }

        const cellStartTime = new Date(args.date)
        const cellEndTime = new Date(cellStartTime)

        const isAvailable = room.slots.some((slot: ObjectType) => {
          const slotStart = new Date(args.date)
          slotStart.setHours(
            parseInt(slot.start_time.split(':')[0], 10),
            parseInt(slot.start_time.split(':')[1], 10),
            0,
            0,
          )

          const slotEnd = new Date(args.date)
          slotEnd.setHours(
            parseInt(slot.end_time.split(':')[0], 10),
            parseInt(slot.end_time.split(':')[1], 10),
            0,
            0,
          )

          return cellStartTime >= slotStart && cellEndTime < slotEnd
        })

        if (!isAvailable) {
          args.element.classList.add('e-disabled-cell')
        } else if (args.elementType === 'monthCells') {
          const cellDate = args.date
          if (cellDate && cellDate.getHours() >= 18) {
            args.element.style.backgroundColor = '#FFFFFF'
          }
        }
      },
      [resources, rooms],
    )

    const majorSlotTemplate = useCallback((props: any) => {
      return <div className='e-time-slot-wrap'>{dayjs(props.date).format('h:mm A')}</div>
    }, [])

    const selectedDate = useMemo(() => {
      if (!day || !timeZone) return new Date()
      return dayjs(day).tz(timeZone).toDate()
    }, [day, timeZone])

    const timeScale = useMemo(
      () => ({ interval, slotCount: 1, majorSlotTemplate }),
      [interval, majorSlotTemplate],
    )

    const workHours = useMemo(
      () => ({
        highlight: true,
        start: dayInterval.min as string,
        end: dayInterval.max as string,
      }),
      [dayInterval.min, dayInterval.max],
    )

    const eventSettings = useMemo(
      () => ({
        dataSource: data,
        enableTooltip: false,
        enableIndicator: true,
        fields: {
          startTime: { title: 'From', name: 'start_time' },
          endTime: { title: 'To', name: 'end_time' },
        },
      }),
      [data],
    )

    const group = useMemo(() => ({ resources: ['Rooms'], enableCompactView: false }), [])

    const handlePopupOpen = useCallback((props: any) => {
      if (props.type === 'Editor') {
        props.cancel = true
      }
    }, [])
    const handleRemoveSelectedCells = useCallback(() => {
      document.querySelectorAll('.e-selected-cell')?.forEach((el) => {
        el.classList.remove('e-selected-cell')
      })
    }, [])

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (
          scheduleContainerRef.current &&
          !scheduleContainerRef.current.contains(event.target as Node)
        ) {
          handleRemoveSelectedCells()
        }
      }

      document.addEventListener('click', handleClickOutside)

      return () => {
        document.removeEventListener('click', handleClickOutside)
      }
    }, [scheduleObj.current, handleRemoveSelectedCells])

    const scheduleHeight = useScheduleHeight()

    return (
      <div className='schedule-control-section' ref={scheduleContainerRef}>
        <div className='col-lg-12 control-section'>
          <div className={`control-wrapper ${!timeZone ? 'loading' : ''}`}>
            {resources.length > 0 && timeZone && (
              <ScheduleComponent
                ref={scheduleObj}
                cssClass='room-schedule'
                width='100%'
                height={scheduleHeight}
                timezone={timeZone}
                timeScale={timeScale}
                selectedDate={selectedDate}
                startHour={dayInterval.min as string}
                endHour={dayInterval.max as string}
                workHours={workHours}
                currentView='Day'
                headerIndentTemplate={indentTemplate}
                resourceHeaderTemplate={resourceHeaderTemplate}
                eventRendered={eventRendered}
                showQuickInfo={false}
                eventClick={handleEventClick}
                actionBegin={handleChange}
                showHeaderBar={false}
                select={handleSelect}
                renderCell={handleRenderCell}
                popupOpen={handlePopupOpen}
                allowDragAndDrop
                eventSettings={eventSettings}
                group={group}>
                <ResourcesDirective>
                  <ResourceDirective
                    field='room_id'
                    title='Rooms'
                    name='Rooms'
                    dataSource={resources}
                    idField='resourceId'
                  />
                </ResourcesDirective>
                <ViewsDirective>
                  <ViewDirective option='Day' eventTemplate={eventTemplate} />
                </ViewsDirective>
                <Inject services={[Day, Resize, DragAndDrop]} />
              </ScheduleComponent>
            )}

            <Show
              when={
                !DateService.isPast(new Date(`${formattedDate} 23:59`), timeZone) &&
                eventsToSendNotification.length > 0
              }>
              <div className='action'>
                <Button
                  modifier={BUTTON_MODIFIER.QUATERNARY}
                  onClick={handleToogleSendSmsConfirmVisible(true)}
                  loading={isSendingNotifications}>
                  <span className='center' style={{ gap: 8 }}>
                    {!isSendingNotifications && (
                      <img
                        className='actionIcon'
                        src={sendMessageIcon}
                        alt={ALT_CONSTANTS.ACTION_ICON}
                      />
                    )}
                    <span>Notify patients</span>
                  </span>
                </Button>
              </div>
            </Show>
          </div>
        </div>

        <Warning
          open={Boolean(awaitConfirmationEvent)}
          title={INFO_CONSTANTS.ARE_YOU_SURE}
          warning={INFO_CONSTANTS.MULTIPLE_PATIENTS_TITLE}
          okButtonText={INFO_CONSTANTS.YES}
          handleClose={() => setAwaitConfirmationEvent(null)}
          handleConfirm={proceedToUpdateEvents}
          hasCancel
        />

        <Warning
          open={showBusyNotification}
          title={INFO_CONSTANTS.INFO}
          warning={INFO_CONSTANTS.BUSY_ROOM_WARNING}
          handleClose={handleToggleSendBusyNotification(false)}
          okButtonText={BUTTON_CONSTANTS.CLOSE}
          handleConfirm={handleToggleSendBusyNotification(false)}
        />

        <SendSMSConfirmation
          open={isSendSmsConfirmVisible}
          handleConfirm={handleSendNotifications}
          handleClose={handleToggleSendSmsConfirmVisible(false)}
        />
        <AnnounceModal
          open={openAnnounceModal}
          toggleOpen={toggleAnnounceModal}
          rooms={rooms.map((room) => room.id).filter((id) => !!id)}
        />
      </div>
    )
  },
)
